import React, { useState, useCallback, useEffect } from 'react';
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';

import { searchHinovaVeiculo, createSimovsatVehicle, listSimovsatOrganizations, isSimovsatAvailable } from '~/services/api';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { HinovaVeiculo } from '~/types';
import { Table } from '~/components/Table';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';
import VehicleDetails from '../VehicleDetails';

interface Organization {
  id: string;
  email: string;
  company_name: string;
  social_identity?: string;
}

export default function VehicleSearch() {
  const [placa, setPlaca] = useState<string>('');
  const [vehicles, setVehicles] = useState<HinovaVeiculo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<HinovaVeiculo | null>(null);
  const [openVehicleDetails, setOpenVehicleDetails] = useState<boolean>(false);
  
  // Estados para importação
  const [importingVehicleId, setImportingVehicleId] = useState<string | null>(null);
  const [openImportModal, setOpenImportModal] = useState<boolean>(false);
  const [vehicleToImport, setVehicleToImport] = useState<HinovaVeiculo | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [importFormData, setImportFormData] = useState({
    organization_id: '',
    km_min: '',
    km_max: '',
  });

  const searchVehicle = useCallback(async () => {
    if (!placa.trim()) {
      setSeverity('warning');
      setMessageSnack('Por favor, informe uma placa para buscar.');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const response = await searchHinovaVeiculo(placa.trim());

      if (response.status === 200 && response.data) {
        const data = response.data;
        const vehiclesData = Array.isArray(data) ? data : [data];
        setVehicles(vehiclesData);
      } else {
        setVehicles([]);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível buscar veículos. Tente novamente.',
      );
      setOpenSnackbar(true);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [placa]);

  const handleViewDetails = (vehicle: HinovaVeiculo) => {
    setSelectedVehicle(vehicle);
    setOpenVehicleDetails(true);
  };

  const loadOrganizations = useCallback(async () => {
    if (!isSimovsatAvailable()) return;
    try {
      setLoadingOrganizations(true);
      const response = await listSimovsatOrganizations(1, 100);
      if (response.status === 200 && response.data?.data) {
        setOrganizations(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar organizações:', err);
    } finally {
      setLoadingOrganizations(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);


  const handleOpenImportModal = (vehicle: HinovaVeiculo) => {
    if (!vehicle.placa) {
      setSeverity('warning');
      setMessageSnack('Veículo sem placa não pode ser importado.');
      setOpenSnackbar(true);
      return;
    }
    setVehicleToImport(vehicle);
    setImportFormData({
      organization_id: '',
      km_min: '',
      km_max: '',
    });
    setOpenImportModal(true);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
    setVehicleToImport(null);
    setImportFormData({
      organization_id: '',
      km_min: '',
      km_max: '',
    });
  };

  const handleImportVehicle = useCallback(async () => {
    if (!vehicleToImport || !vehicleToImport.placa) {
      setSeverity('warning');
      setMessageSnack('Dados do veículo inválidos.');
      setOpenSnackbar(true);
      return;
    }

    if (!importFormData.organization_id || !importFormData.km_min || !importFormData.km_max) {
      setSeverity('warning');
      setMessageSnack('Preencha todos os campos obrigatórios (Organização, KM Mínimo e KM Máximo).');
      setOpenSnackbar(true);
      return;
    }

    try {
      setImportingVehicleId(vehicleToImport.placa);
      
      const vehicleData = {
        license_plate: vehicleToImport.placa,
        organization_id: importFormData.organization_id,
        km_min: Number(importFormData.km_min),
        km_max: Number(importFormData.km_max),
        model: vehicleToImport.modelo || '',
        manufacture: vehicleToImport.marca || '',
        color: vehicleToImport.cor || '',
        vin: vehicleToImport.chassi || '',
        model_year: vehicleToImport.ano ? Number(vehicleToImport.ano) : undefined,
        status: 'ATIVO',
      };

      const response = await createSimovsatVehicle(vehicleData);

      if (response.status === 200 || response.status === 201) {
        setSeverity('success');
        setMessageSnack('Veículo importado com sucesso para o Simovsat.');
        setOpenSnackbar(true);
        handleCloseImportModal();
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('error');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError || 'Erro desconhecido') + ': não foi possível importar o veículo. Tente novamente.',
      );
      setOpenSnackbar(true);
    } finally {
      setImportingVehicleId(null);
    }
  }, [vehicleToImport, importFormData]);

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 20 }} alignItems='flex-end'>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='Placa'
            name='placa'
            type='text'
            value={placa}
            onChange={e => setPlaca(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            error={null}
            placeholder='Ex: ABC1234'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            label='Buscar'
            loading={loading}
            onClick={searchVehicle}
            filled
            style={{ width: '100%', marginTop: 0 }}
          />
        </Grid>
      </Grid>

      <Table
        isLoading={loading}
        data={vehicles}
        columns={{
          placa: {
            title: 'Placa',
          },
          modelo: {
            title: 'Modelo',
          },
          marca: {
            title: 'Marca',
          },
          chassi: {
            title: 'Chassi',
            render: rowData => rowData.chassi || '-',
          },
          renavam: {
            title: 'RENAVAM',
            render: rowData => rowData.renavam || '-',
          },
          tipo_veiculo: {
            title: 'Tipo de Veículo',
            render: rowData => rowData.tipo_veiculo || '-',
          },
          valor_fipe: {
            title: 'Valor FIPE',
            render: rowData => (rowData.valor_fipe ? `R$ ${rowData.valor_fipe.toFixed(2)}` : '-'),
          },
          importacao: {
            title: 'Importação',
            render: rowData => (
              <Button
                label='Importar'
                onClick={() => handleOpenImportModal(rowData as HinovaVeiculo)}
                loading={importingVehicleId === rowData.placa}
                filled
                style={{ minWidth: '100px', fontSize: '0.875rem' }}
              />
            ),
          },
          associado_cpf: {
            title: 'CPF Associado',
            render: rowData => rowData.associado_cpf || '-',
          },
        }}
        actions={[
          {
            icon: () => <span>👁️</span>,
            tooltip: 'Ver detalhes',
            onClick: (_, rowData) => handleViewDetails(rowData as HinovaVeiculo),
          },
        ]}
        localization={{
          body: {
            emptyDataSourceMessage: loading
              ? 'Carregando...'
              : 'Nenhum veículo encontrado. Use o campo de busca para pesquisar por placa.',
          },
          pagination: {
            labelRowsSelect: 'veículos',
          },
        }}
        options={{
          showTitle: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          paging: true,
          filtering: false,
          search: false,
        }}
      />

      {selectedVehicle && (
        <VehicleDetails
          vehicle={selectedVehicle}
          open={openVehicleDetails}
          onClose={() => {
            setOpenVehicleDetails(false);
            setSelectedVehicle(null);
          }}
        />
      )}

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />

      {/* Modal de Importação */}
      <Dialog open={openImportModal} onClose={handleCloseImportModal} maxWidth='sm' fullWidth>
        <DialogTitle>Importar Veículo para Simovsat</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 10 }}>
            <Grid item xs={12}>
              <div style={{ marginBottom: 16 }}>
                <strong>Placa:</strong> {vehicleToImport?.placa || '-'}
              </div>
              <div style={{ marginBottom: 16 }}>
                <strong>Modelo:</strong> {vehicleToImport?.modelo || '-'}
              </div>
              <div style={{ marginBottom: 16 }}>
                <strong>Marca:</strong> {vehicleToImport?.marca || '-'}
              </div>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Organização</InputLabel>
                <Select
                  value={importFormData.organization_id}
                  onChange={e => setImportFormData({ ...importFormData, organization_id: e.target.value as string })}
                  disabled={loadingOrganizations}
                >
                  {organizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='KM Mínimo'
                type='number'
                value={importFormData.km_min}
                onChange={e => setImportFormData({ ...importFormData, km_min: e.target.value })}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='KM Máximo'
                type='number'
                value={importFormData.km_max}
                onChange={e => setImportFormData({ ...importFormData, km_max: e.target.value })}
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button label='Cancelar' onClick={handleCloseImportModal} />
          <Button
            label='Importar'
            onClick={handleImportVehicle}
            loading={importingVehicleId !== null}
            filled
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
